import FilterAltIcon from '@mui/icons-material/FilterAlt'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import axios from 'api/axios'
import { useEffect, useState } from 'react'

export default function FiltrosGantt({ onApplyFilters }) {
  const [open, setOpen] = useState(false)

  // Estados de los filtros
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [tiposMaquinaSeleccionados, setTiposMaquinaSeleccionados] = useState([])
  const [seccionesSeleccionadas, setSeccionesSeleccionadas] = useState([])

  // Opciones disponibles
  const [tiposMaquinaDisponibles, setTiposMaquinaDisponibles] = useState([])
  const [seccionesDisponibles, setSeccionesDisponibles] = useState([])

  const cargarOpcionesMaquinas = async () => {
    try {
      // Cargar desde los catálogos en paralelo
      const [responseTipos, responseSecciones] = await Promise.all([
        axios.get('/optprod/adm/tipos-maquina/'),
        axios.get('/optprod/adm/secciones-carga/'),
      ])

      // Filtrar solo los que tienen valores válidos
      const tiposValidos = responseTipos.data.filter((t) => t.tipo_maquina && t.desc_tipo_maquina)
      const seccionesValidas = responseSecciones.data.filter(
        (s) => s.codigo_seccion && s.desc_seccion,
      )

      setTiposMaquinaDisponibles(tiposValidos)
      setSeccionesDisponibles(seccionesValidas)
    } catch (error) {
      // Error handled
    }
  }

  // Cargar opciones de máquinas al abrir el modal
  useEffect(() => {
    if (open) {
      cargarOpcionesMaquinas()
    }
  }, [open])

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleAplicar = () => {
    onApplyFilters({
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      tipos_maquina: tiposMaquinaSeleccionados,
      secciones: seccionesSeleccionadas,
    })
    handleClose()
  }

  const handleLimpiar = () => {
    setFechaDesde('')
    setFechaHasta('')
    setTiposMaquinaSeleccionados([])
    setSeccionesSeleccionadas([])
    // Aplicar sin filtros (mostrar todo)
    onApplyFilters({
      fecha_desde: '',
      fecha_hasta: '',
      tipos_maquina: [],
      secciones: [],
    })
  }

  return (
    <>
      <Tooltip title="Filtrar Gantt">
        <IconButton onClick={handleOpen} color="primary" size="large">
          <FilterAltIcon />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Filtrar Planificación</DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Fecha Desde */}
            <TextField
              label="Fecha Desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              fullWidth
              helperText="Filtra actividades que comienzan desde esta fecha"
            />

            {/* Fecha Hasta */}
            <TextField
              label="Fecha Hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
              fullWidth
              helperText="Filtra actividades que comienzan hasta esta fecha"
            />

            {/* Tipo de Máquina (multi-select) */}
            <FormControl fullWidth>
              <InputLabel>Tipo de Máquina</InputLabel>
              <Select
                multiple
                value={tiposMaquinaSeleccionados}
                onChange={(e) => setTiposMaquinaSeleccionados(e.target.value)}
                input={<OutlinedInput label="Tipo de Máquina" />}
                renderValue={(selected) => {
                  // Mostrar las descripciones de los tipos seleccionados
                  return selected
                    .map((codigo) => {
                      const item = tiposMaquinaDisponibles.find((t) => t.tipo_maquina === codigo)
                      return item?.desc_tipo_maquina || codigo
                    })
                    .join(', ')
                }}
              >
                {tiposMaquinaDisponibles.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.tipo_maquina}>
                    <Checkbox checked={tiposMaquinaSeleccionados.includes(tipo.tipo_maquina)} />
                    <ListItemText primary={tipo.desc_tipo_maquina || tipo.tipo_maquina} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sección (multi-select) */}
            <FormControl fullWidth>
              <InputLabel>Sección</InputLabel>
              <Select
                multiple
                value={seccionesSeleccionadas}
                onChange={(e) => setSeccionesSeleccionadas(e.target.value)}
                input={<OutlinedInput label="Sección" />}
                renderValue={(selected) => {
                  // Mostrar las descripciones de las secciones seleccionadas
                  return selected
                    .map((codigo) => {
                      const item = seccionesDisponibles.find((s) => s.codigo_seccion === codigo)
                      return item?.desc_seccion || codigo
                    })
                    .join(', ')
                }}
              >
                {seccionesDisponibles.map((seccion) => (
                  <MenuItem key={seccion.id} value={seccion.codigo_seccion}>
                    <Checkbox checked={seccionesSeleccionadas.includes(seccion.codigo_seccion)} />
                    <ListItemText primary={seccion.desc_seccion || seccion.codigo_seccion} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleLimpiar} color="inherit">
            Limpiar Filtros
          </Button>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleAplicar} color="contained">
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
